import { useEffect, useState } from 'react'
import { UserCog, Loader2, Plus, Copy, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

interface Superadmin {
  id: string
  email: string
  display_name: string
  role: string
  totp_enabled: boolean
  last_login_at: string | null
  locked_until: string | null
  created_at: string
}

interface CreatedAdmin {
  id: string
  email: string
  display_name: string
  role: string
  totp_secret: string
  provisioning_uri: string
}

const roleColor = (role: string) => {
  switch (role) {
    case 'founder':
      return 'bg-violet-500/10 text-violet-500'
    case 'platform_admin':
      return 'bg-blue-500/10 text-blue-500'
    case 'abuse_analyst':
      return 'bg-amber-500/10 text-amber-500'
    default:
      return 'bg-gray-500/10 text-gray-500'
  }
}

export function SuperadminManagement() {
  const [admins, setAdmins] = useState<Superadmin[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [created, setCreated] = useState<CreatedAdmin | null>(null)
  const [form, setForm] = useState({
    email: '',
    password: '',
    display_name: '',
    role: 'support',
  })

  const fetchAdmins = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/superadmins')
      setAdmins(res.data.superadmins || [])
    } catch {
      toast.error('Failed to load superadmins')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAdmins()
  }, [])

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.display_name) {
      toast.error('All fields are required')
      return
    }
    try {
      const res = await api.post('/admin/superadmins', form)
      setCreated(res.data)
      setCreateOpen(false)
      setForm({ email: '', password: '', display_name: '', role: 'support' })
      fetchAdmins()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to create superadmin'))
    }
  }

  const handleRoleChange = async (adminId: string, newRole: string) => {
    try {
      await api.patch(`/admin/superadmins/${adminId}/role`, { role: newRole })
      toast.success('Role updated')
      fetchAdmins()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to update role'))
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <UserCog className="h-5 w-5 text-primary" />
            Superadmins
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage admin roles and access</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Superadmin
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Superadmin</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="admin@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                />
                <p className="text-xs text-muted-foreground">Minimum 12 characters.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(value) => setForm({ ...form, role: value })}
                >
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="support">Support</SelectItem>
                    <SelectItem value="abuse_analyst">Abuse Analyst</SelectItem>
                    <SelectItem value="platform_admin">Platform Admin</SelectItem>
                    <SelectItem value="founder">Founder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Name</th>
                  <th className="px-4 py-3 text-left font-medium">Role</th>
                  <th className="px-4 py-3 text-left font-medium">2FA</th>
                  <th className="px-4 py-3 text-left font-medium">Last Login</th>
                  <th className="px-4 py-3 text-left font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : admins.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No superadmins
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium">{admin.display_name}</div>
                        <div className="text-xs text-muted-foreground">{admin.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={admin.role}
                          onValueChange={(value) => handleRoleChange(admin.id, value)}
                        >
                          <SelectTrigger className="w-40 h-8">
                            <Badge variant="secondary" className={roleColor(admin.role)}>
                              {admin.role}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="support">support</SelectItem>
                            <SelectItem value="abuse_analyst">abuse_analyst</SelectItem>
                            <SelectItem value="platform_admin">platform_admin</SelectItem>
                            <SelectItem value="founder">founder</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        {admin.totp_enabled ? (
                          <span className="text-emerald-500 text-xs">Enabled</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">Disabled</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {admin.last_login_at ? new Date(admin.last_login_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(admin.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              Superadmin Created
            </DialogTitle>
          </DialogHeader>
          {created && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Share these credentials securely with <strong>{created.display_name}</strong>.
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{created.email}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Role</span>
                  <span className="font-medium capitalize">{created.role}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>TOTP Secret</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted rounded px-2 py-1 text-xs break-all">
                    {created.totp_secret}
                  </code>
                  <Button variant="ghost" size="icon" onClick={() => copyToClipboard(created.totp_secret)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Provisioning URI</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted rounded px-2 py-1 text-xs break-all">
                    {created.provisioning_uri}
                  </code>
                  <Button variant="ghost" size="icon" onClick={() => copyToClipboard(created.provisioning_uri)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
